import { format, parseISO, isValid } from 'date-fns';

/**
 * Helpers for HTML date / datetime-local inputs.
 *
 * Never use `toISOString().slice(0, 16)` for datetime-local values — that is UTC
 * and causes picker vs display mismatches (e.g. 8:00 AM selected, field shows 2:00 PM).
 */

function asDate(value: string | Date | null | undefined): Date | null {
  if (value == null || value === '') return null;
  if (value instanceof Date) return isValid(value) ? value : null;
  // date-only YYYY-MM-DD — parse as local calendar day (avoid UTC midnight shift)
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    const [y, m, d] = value.split('-').map(Number);
    const local = new Date(y, m - 1, d);
    return isValid(local) ? local : null;
  }
  const parsed = parseISO(value);
  if (isValid(parsed)) return parsed;
  const fallback = new Date(value);
  return Number.isNaN(fallback.getTime()) ? null : fallback;
}

/** `value=` for `<input type="datetime-local">` — local wall clock */
export function toDatetimeLocalValue(value: string | Date | null | undefined): string {
  const d = asDate(value);
  if (!d) return '';
  return format(d, "yyyy-MM-dd'T'HH:mm");
}

/**
 * `onChange` from datetime-local → ISO string for API storage.
 * Browsers parse `YYYY-MM-DDTHH:mm` as local time.
 */
export function fromDatetimeLocalValue(local: string): string | null {
  if (!local) return null;
  const d = new Date(local);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

/** `value=` / default for `<input type="date">` — local calendar day */
export function toDateInputValue(value: string | Date | null | undefined): string {
  if (value == null || value === '') return '';
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}/.test(value)) {
    return value.slice(0, 10);
  }
  const d = asDate(value);
  if (!d) return '';
  return format(d, 'yyyy-MM-dd');
}

/** Today as YYYY-MM-DD in the user's local timezone */
export function todayDateInputValue(): string {
  return format(new Date(), 'yyyy-MM-dd');
}

/**
 * Normalize a date input string for API date-only columns.
 * Prefer YYYY-MM-DD (no UTC midnight ISO) to avoid off-by-one on DATEONLY fields.
 */
export function toDateOnly(value: string | Date | null | undefined): string | null {
  if (value == null || value === '') return null;
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}/.test(value)) {
    return value.slice(0, 10);
  }
  const d = asDate(value);
  if (!d) return null;
  return format(d, 'yyyy-MM-dd');
}
